import React from "react";
import {
  CheckCircle,
  AlertTriangle,
  Shield,
  BookOpen,
  ArrowRight,
  MapPin,
  Scale,
  Layers,
  RefreshCw,
} from "lucide-react";

// --- TYPES ---

interface RedactionField {
  field: string;
  format: string;
}

interface DocumentComponent {
  document_type: string;
  abbreviation?: string;
  primary_purpose: string;
  start_page: number | string;
  end_page: number | string;
}

interface AnalysisData {
  document_analysis: DocumentComponent[];
  jurisdiction_and_context: {
    jurisdiction_state: string;
    jurisdiction_court: string;
    case_type: string;
    defendant_residence_state: string;
  };
  redaction_requirements: {
    governing_law: string;
    mandatory_redactions: RedactionField[];
  };
  meta?: {
    version?: number;
    reanalyzed?: boolean;
  };
}

interface AnalysisResultCardProps {
  result: AnalysisData;
  onConfirm: () => void;
  onRescan?: () => void;
  rescanning?: boolean;
  cacheHit?: boolean; // 👈 pass from backend
}

const FEDERAL_FALLBACK_RULES: RedactionField[] = [
  { field: "SSN", format: "Full redaction (FRCP 5.2)" },
  { field: "DOB", format: "Keep year only (FRCP 5.2)" },
  { field: "ACCOUNT_NUMBER", format: "Keep last 4 (FRCP 5.2)" },
  { field: "CREDIT_CARD", format: "Keep last 4 (FRCP 5.2)" },
  { field: "EMAIL", format: "Full redaction (General Privacy)" },
  { field: "PASSWORD", format: "Full redaction (account secret)" },
];

export default function AnalysisResultCard({
  result,
  onConfirm,
  onRescan,
  rescanning = false,
  cacheHit = false,
}: AnalysisResultCardProps) {
  const {
    document_analysis = [],
    jurisdiction_and_context,
    redaction_requirements,
    meta,
  } = result;
  const safeJurisdiction = jurisdiction_and_context || {
    jurisdiction_state: "Unknown",
    jurisdiction_court: "Unknown",
    case_type: "Unknown",
    defendant_residence_state: "Unknown",
  };
  const safeRedactionRequirements = redaction_requirements || {
    governing_law: "Unknown",
    mandatory_redactions: [],
  };
  const { mandatory_redactions } = safeRedactionRequirements;
  const stateCode = safeJurisdiction.jurisdiction_state || "Unknown";
  const hasValidRulesArray = Array.isArray(mandatory_redactions);
  const useFederalFallback =
    !hasValidRulesArray ||
    mandatory_redactions.length === 0 ||
    stateCode === "Unknown";
  const rulesToShow = useFederalFallback
    ? FEDERAL_FALLBACK_RULES
    : mandatory_redactions;

  const isStrict =
    rulesToShow.length > 3 ||
    safeRedactionRequirements.governing_law.toLowerCase().includes("strict");

  const docCount = document_analysis.length;

  return (
    <div className="relative w-full max-w-5xl mx-auto bg-slate-900 border border-slate-700 rounded-2xl overflow-hidden shadow-2xl font-sans">
      {/* 🔄 RE-SCAN OVERLAY */}
      {rescanning && (
        <div className="absolute inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex flex-col items-center justify-center">
          <RefreshCw className="w-10 h-10 text-blue-400 animate-spin mb-4" />
          <p className="text-slate-200 font-semibold">Re-analyzing document…</p>
          <p className="text-slate-400 text-sm mt-1">
            This may take a few seconds
          </p>
        </div>
      )}

      {/* HEADER */}
      <div
        className={`p-6 flex flex-col sm:flex-row sm:items-center justify-between border-b gap-4
        ${isStrict ? "bg-red-900/20 border-red-900/50" : "bg-blue-900/20 border-blue-900/50"}`}
      >
        <div className="flex items-center space-x-4">
          <div
            className={`p-3 rounded-xl ${isStrict ? "bg-red-500/20 text-red-400" : "bg-blue-500/20 text-blue-400"}`}
          >
            <Shield className="w-8 h-8" />
          </div>

          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              {useFederalFallback ? "Federal" : stateCode} Compliance Protocol
              {isStrict && (
                <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded-full">
                  Strict Redaction
                </span>
              )}
            </h2>

            <div className="flex items-center gap-3 mt-1 flex-wrap">
              <p className="text-slate-400 text-sm flex items-center gap-1">
                <Scale className="w-3 h-3" />
                {safeRedactionRequirements.governing_law}
              </p>

              <span className="text-xs bg-slate-800 text-slate-300 px-2 py-0.5 rounded-full border border-slate-600 flex items-center gap-1">
                <Layers className="w-3 h-3" />
                {docCount} Document{docCount !== 1 ? "s" : ""}
              </span>

              {/* 🏷️ CACHE / FRESH BADGE */}
              <span
                className={`text-xs px-2 py-0.5 rounded-full border
                ${
                  cacheHit
                    ? "bg-yellow-500/10 text-yellow-400 border-yellow-500/30"
                    : meta?.reanalyzed
                      ? "bg-purple-500/10 text-purple-400 border-purple-500/30"
                      : "bg-green-500/10 text-green-400 border-green-500/30"
                }`}
              >
                {cacheHit
                  ? "Cached Analysis"
                  : meta?.reanalyzed
                    ? "Re-analysis"
                    : "Fresh Analysis"}
              </span>

              {meta?.version && (
                <span className="text-xs text-slate-400">v{meta.version}</span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 bg-slate-800/50 px-4 py-2 rounded-lg border border-slate-700">
          <MapPin className="w-4 h-4 text-blue-400" />
          <div>
            <p className="text-slate-500 text-[10px] uppercase tracking-widest font-bold">
              Filing Venue
            </p>
            <p className="text-white font-medium">
              {useFederalFallback ? "FEDERAL" : stateCode}
            </p>
          </div>
        </div>
      </div>

      {/* BODY */}
      <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
        {/* LEFT */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-slate-800 rounded-xl p-5 border border-slate-700/50 max-h-[400px] overflow-y-auto">
            {document_analysis.map((doc, idx) => {
              const title = doc.abbreviation
                ? `${doc.document_type} - ${doc.abbreviation}`
                : doc.document_type;

              return (
                <div
                  key={idx}
                  className="mb-3 bg-slate-900/50 p-3 rounded-lg border border-slate-700/50"
                >
                  <div className="flex justify-between text-sm font-semibold text-white">
                    <span>{title}</span>
                    <span className="text-xs text-slate-400">
                      Pg {doc.start_page}-{doc.end_page}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 mt-1 line-clamp-2">
                    {doc.primary_purpose}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="bg-slate-800 rounded-xl p-5 border border-slate-700/50">
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Case Type</span>
              <span className="text-slate-300">
                {safeJurisdiction.case_type}
              </span>
            </div>
          </div>
        </div>

        {/* RIGHT */}
        <div className="lg:col-span-2 flex flex-col">
          <div className="bg-slate-800/30 rounded-xl border border-slate-700 flex-grow mb-6">
            {rulesToShow.map((item, idx) => (
              <div
                key={idx}
                className="p-4 flex gap-3 border-b border-slate-700/50"
              >
                <CheckCircle className="w-5 h-5 text-green-500" />
                <div>
                  <p className="text-white text-sm font-medium">{item.field}</p>
                  <p className="text-xs text-slate-500">
                    Format: {item.format}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* ACTIONS */}
          <div className="flex flex-col sm:flex-row gap-3">
            {onRescan && (
              <button
                onClick={onRescan}
                disabled={rescanning}
                className="px-6 py-4 rounded-xl border border-slate-600 text-slate-300 bg-slate-800 hover:bg-slate-700 disabled:opacity-60"
              >
                Re-analyze document
              </button>
            )}

            <button
              onClick={onConfirm}
              disabled={rescanning}
              className="flex-1 bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white font-bold py-4 rounded-xl flex items-center justify-center"
            >
              {stateCode === "Unknown"
                ? "Apply Default Strategy"
                : `Apply ${safeJurisdiction.jurisdiction_state} Strategy`}
              <ArrowRight className="w-5 h-5 ml-2" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
